'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Customer } from '@/lib/shopify/types';
import {
  customerAccessTokenCreateClient,
  customerAccessTokenDeleteClient,
  customerCreateClient,
  customerRecoverClient,
  getCustomerClient,
} from '@/lib/shopify/client';

interface AuthContextType {
  customer: Customer | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  register: (input: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) => Promise<{ ok: boolean; error?: string }>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
export const CUSTOMER_TOKEN_KEY = 'shopify_customer_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const token =
        typeof window !== 'undefined' ? localStorage.getItem(CUSTOMER_TOKEN_KEY) : null;
      if (!token) {
        setCustomer(null);
        return;
      }

      const nextCustomer = await getCustomerClient(token);
      if (!nextCustomer) {
        localStorage.removeItem(CUSTOMER_TOKEN_KEY);
      }
      setCustomer(nextCustomer);
    } catch {
      setCustomer(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refresh();
      setIsLoading(false);
    })();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const { token, error } = await customerAccessTokenCreateClient(email, password);
      if (!token) return { ok: false, error: error || 'Could not sign in' };
      localStorage.setItem(CUSTOMER_TOKEN_KEY, token.accessToken);
      await refresh();
      return { ok: true };
    },
    [refresh]
  );

  const register = useCallback(
    async (input: { email: string; password: string; firstName?: string; lastName?: string }) => {
      const created = await customerCreateClient(input);
      if (!created.success) {
        return { ok: false, error: created.error || 'Could not create account' };
      }
      const signedIn = await customerAccessTokenCreateClient(input.email, input.password);
      if (!signedIn.token) {
        return {
          ok: false,
          error: signedIn.error || 'Account created. Please sign in to continue.',
        };
      }
      localStorage.setItem(CUSTOMER_TOKEN_KEY, signedIn.token.accessToken);
      await refresh();
      return { ok: true };
    },
    [refresh]
  );

  const logout = useCallback(async () => {
    const token = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    localStorage.removeItem(CUSTOMER_TOKEN_KEY);
    if (token) {
      await customerAccessTokenDeleteClient(token).catch(() => undefined);
    }
    setCustomer(null);
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    await customerRecoverClient(email);
  }, []);

  return (
    <AuthContext.Provider
      value={{ customer, isLoading, login, register, logout, forgotPassword, refresh }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
